import { Check, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { type FormEvent, useState } from 'react';
import type { CandidateStage, JobDescription } from '../types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export interface NewCandidateInput {
  name: string;
  email: string;
  phone: string;
  role: string;
  jdId: string;
  stage: CandidateStage;
  status: 'Active';
}

interface AddCandidateModalProps {
  isOpen: boolean;
  jds: JobDescription[];
  onClose: () => void;
  onSubmit: (candidate: NewCandidateInput) => void;
}

const initialCandidate: NewCandidateInput = {
  name: '',
  email: '',
  phone: '',
  role: '',
  jdId: '',
  stage: 'New',
  status: 'Active',
};

export default function AddCandidateModal({
  isOpen,
  jds,
  onClose,
  onSubmit,
}: AddCandidateModalProps) {
  const [newCandidate, setNewCandidate] = useState<NewCandidateInput>(initialCandidate);

  const closeModal = () => {
    setNewCandidate(initialCandidate);
    onClose();
  };

  const handleAddSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(newCandidate);
    setNewCandidate(initialCandidate);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-card rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-border-subtle"
          >
            <div className="p-6 border-b border-border-subtle flex items-center justify-between bg-bg-main/50">
              <div>
                <h2 className="text-2xl font-bold text-text-main">Add New Candidate</h2>
                <p className="text-base text-text-subtle">Enter the candidate details manually.</p>
              </div>
              <Button variant="ghost" size="icon-sm" type="button" onClick={closeModal}>
                <X size={20} />
              </Button>
            </div>
            <form onSubmit={handleAddSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-base font-semibold text-text-main mb-1.5">
                    Full Name
                  </label>
                  <Input
                    required
                    type="text"
                    placeholder="e.g. John Doe"
                    value={newCandidate.name}
                    onChange={(e) => setNewCandidate({ ...newCandidate, name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base font-semibold text-text-main mb-1.5">
                      Email Address
                    </label>
                    <Input
                      required
                      type="email"
                      placeholder="john@example.com"
                      value={newCandidate.email}
                      onChange={(e) =>
                        setNewCandidate({
                          ...newCandidate,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-base font-semibold text-text-main mb-1.5">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={newCandidate.phone}
                      onChange={(e) =>
                        setNewCandidate({
                          ...newCandidate,
                          phone: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-base font-semibold text-text-main mb-1.5">
                    Job Position
                  </label>
                  <Select
                    value={newCandidate.jdId}
                    onValueChange={(value) => setNewCandidate({ ...newCandidate, jdId: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a position..." />
                    </SelectTrigger>
                    <SelectContent>
                      {jds.map((jd) => (
                        <SelectItem key={jd.id} value={jd.id}>
                          {jd.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="pt-4 flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 gap-2">
                  <Check size={18} />
                  Create Candidate
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
